/**
 * The target amount as the client types it.
 *
 * The field shows thousands separators as you type ("2,500,000") and takes the
 * shorthands a GP actually says ("2.5m", "500k"). The draft keeps that text; only
 * `parseAmount` turns it into the number the API stores, in whole units (never
 * ×100, see the feature README). Pure; no React, no Redux.
 */

const MULTIPLIERS: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9 };

/** Longest integer part accepted while typing: up to 9,999,999,999,999. */
const MAX_INTEGER_DIGITS = 13;

/**
 * The amount a text stands for, or `NaN` when it is not one.
 * "2,500,000" → 2500000 · "2.5m" → 2500000 · "500k" → 500000 · "" → NaN.
 */
export function parseAmount(text: string): number {
  const compact = text.replace(/[\s,]/g, '');
  const match = /^(\d+(?:\.\d*)?|\.\d+)([kmbKMB])?$/.exec(compact);
  if (!match) return Number.NaN;
  const multiplier = match[2] ? MULTIPLIERS[match[2].toLowerCase()] : 1;
  return Math.round(Number.parseFloat(match[1]) * multiplier * 100) / 100;
}

/** An amount as the field shows it: grouped, no decimals beyond what it has. */
export function formatAmountInput(amount: number): string {
  return amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Rewrite the field's text after a keystroke: a completed shorthand ("2.5m")
 * expands to its full figure, anything else is regrouped. Letters other than a
 * trailing k/m/b are dropped, and at most two decimals are kept.
 */
export function formatTyping(raw: string): string {
  const compact = raw.replace(/[\s,]/g, '');
  const shorthand = /^(\d+(?:\.\d*)?|\.\d+)([kmbKMB])$/.exec(compact);
  if (shorthand) {
    return formatAmountInput(
      Number.parseFloat(shorthand[1]) * MULTIPLIERS[shorthand[2].toLowerCase()],
    );
  }

  let digits = compact.replace(/[^\d.]/g, '');
  const dot = digits.indexOf('.');
  if (dot >= 0) {
    digits =
      digits.slice(0, dot + 1) +
      digits
        .slice(dot + 1)
        .replace(/\./g, '')
        .slice(0, 2);
  }
  const [integer = '', decimals] = digits.split('.');
  const trimmed = integer.replace(/^0+(?=\d)/, '').slice(0, MAX_INTEGER_DIGITS);
  const grouped = trimmed
    ? Number(trimmed).toLocaleString('en-US')
    : decimals !== undefined
      ? '0'
      : '';
  return decimals !== undefined ? `${grouped}.${decimals}` : grouped;
}
